import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import journalValidation from 'donor-elf-web/validations/journal';
import JournalGoalValidation from 'donor-elf-web/validations/journal-goal';
import Router from '@ember/routing/router';
import type { JournalEditRouteModel } from 'donor-elf-web/routes/funds/fund/journals/journal/edit';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import CampaignService from 'donor-elf-web/services/campaign-service';

export default class JournalEditController extends Controller {
  @service declare campaignService: CampaignService;
  @service declare fundsService: any;
  @service declare journalService: any;
  @service declare router: Router;

  @tracked error: any;
  @tracked saving = false;
  @tracked showValidationErrors = false;
  @tracked journalChangeset: any;
  @tracked goalChangesets: any[] = [];

  declare model: JournalEditRouteModel;

  get campaignSelectOptions(): SelectOption[] {
    return [
      { val: undefined, label: 'None' },
      ...this.campaignService.sortedCampaignSelectOptions,
    ];
  }

  get showCampaignDropDown() {
    const { campaignsEnabled, campaigns } = this.campaignService;
    return campaignsEnabled && campaigns.length > 0;
  }

  get showCampaignChangedWarning() {
    const newCampaignId = this.journalChangeset?.get('campaignId') ?? 'None';
    return (
      newCampaignId !== 'None' &&
      newCampaignId !== this.model.journalInfo.journal.campaignId
    );
  }

  get showCampaignRemovedWarning() {
    const newCampaignId = this.journalChangeset?.get('campaignId') ?? 'None';

    return (
      newCampaignId === 'None' && this.model.journalInfo.journal.campaignId
    );
  }

  get showDecisionsRequireGift() {
    return (
      !this.model.template.hideDecisionsRequireGift &&
      !this.model.template.decisionsRequireGift
    );
  }

  @action
  templateDidInsert() {
    this.error = null;
    this.saving = false;
    this.showValidationErrors = false;
    this.journalChangeset = new Changeset(
      this.model.journalInfo.journal,
      lookupValidator(journalValidation),
      journalValidation,
    );
    this.goalChangesets = this.model.journalInfo.goals.map(
      (x: any) =>
        new Changeset(
          x,
          lookupValidator(JournalGoalValidation),
          JournalGoalValidation,
        ),
    );
  }

  @action
  async handleSave() {
    this.showValidationErrors = true;
    await this.journalChangeset.validate();
    for (let i = 0; i < this.goalChangesets.length; i++) {
      await this.goalChangesets[i].validate();
    }
    if (!this.journalChangeset.get('isValid')) {
      return;
    }
    if (this.goalChangesets.find((x) => !x.get('isValid'))) {
      return;
    }
    this.showValidationErrors = false;
    this.saving = true;
    try {
      await this.fundsService.confirmValidFundToken();
      await this.journalChangeset.save();
      for (let i = 0; i < this.goalChangesets.length; i++) {
        await this.goalChangesets[i].save();
      }
      this.router.transitionTo('funds.fund.journals.journal.index');
    } catch (error) {
      this.error = error;
    }
  }
}
